/**
 * Fiscal-year and "today" helpers on plain `YYYY-MM-DD` strings — no `Date`
 * getters, so the answer never depends on the process timezone. The web
 * adapter (`apps/web/src/lib/fiscalYear.ts`) and Ré's report tools
 * (`apps/api/src/lib/bookkeeperReportTools.ts`) share this one rule.
 */
import { addDaysYmd } from "../format/date.js";
const pad2 = (n) => String(n).padStart(2, "0");
/** `organization_tax_settings.fiscal_year_start_month` (1–12); anything else is January. */
function normalizeStartMonth(startMonth) {
    return Number.isInteger(startMonth) && startMonth >= 1 && startMonth <= 12 ? startMonth : 1;
}
/**
 * The fiscal year containing `todayYmd`, for an org whose fiscal year starts
 * on the first of `startMonth`. The WHOLE year, not year-to-date — like the
 * date filter's "This Year": a trial balance over the fiscal year includes
 * what is already booked after today. Both ends inclusive.
 */
export function fiscalYearRange(startMonth, todayYmd) {
    const month = normalizeStartMonth(startMonth);
    const [year, todayMonth] = todayYmd.split("-").map(Number);
    const startYear = todayMonth >= month ? year : year - 1;
    const startDate = `${startYear}-${pad2(month)}-01`;
    // The day before the next fiscal year's first day.
    const endDate = addDaysYmd(`${startYear + 1}-${pad2(month)}-01`, -1);
    return { startDate, endDate };
}
/**
 * Today's calendar day (`YYYY-MM-DD`) as it reads in the org's timezone at
 * `now`. A null / empty zone, or one `Intl` does not know
 * (`organizations.timezone` is free text), reads in UTC.
 */
export function orgTodayYmd(timezone, now) {
    const partsIn = (timeZone) => new Intl.DateTimeFormat("en-US", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(now);
    let parts;
    try {
        parts = partsIn(timezone || "UTC");
    }
    catch {
        // `Intl` raises RangeError on an unknown zone.
        parts = partsIn("UTC");
    }
    const read = (type) => parts.find((p) => p.type === type)?.value ?? "";
    return `${read("year")}-${read("month")}-${read("day")}`;
}
/**
 * The last day of the fiscal year BEFORE the one containing `asOfYmd` — the
 * balance sheet's comparative date (IAS 34.20(a): an interim statement of
 * financial position is compared with the end of the immediately preceding
 * financial year), and also the day its prior years' result is read through.
 * At a fiscal year-end this is exactly one year earlier.
 *
 * `asOfYmd` must be a real `YYYY-MM-DD` day (every caller is downstream of a
 * validated request). Uses the org's CURRENT start month: after a change of
 * financial year (a long or short year) the date differs from the historical
 * year-end — the document prints the date, so it never misstates which day it
 * drew.
 */
export function previousFiscalYearEnd(startMonth, asOfYmd) {
    return addDaysYmd(fiscalYearRange(startMonth, asOfYmd).startDate, -1);
}
//# sourceMappingURL=fiscalYear.js.map